/**
 * CMS-09 voucher generator: N unique `AKP-XXXX-XXXX` codes in one call. The server owns the
 * alphabet and the uniqueness check (docs/07 §3); the drawer lists what came back so the operator
 * can copy it into whatever channel is selling them.
 */
import { type GrantDTO, type GrantTxType, VoucherPrefix } from '@akpl/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../../api';
import { useT } from '../../i18n';
import { Button } from '../ui/button';
import { Drawer } from '../ui/drawer';
import { Field, Input, Select } from '../ui/field';
import { useToast } from '../ui/toast';

const TX_TYPES: GrantTxType[] = ['retail', 'bundle', 'gift', 'promo', 'manual'];

export function VoucherDrawer({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [count, setCount] = useState('12');
  const [prefix, setPrefix] = useState('AKP');
  const [durationDays, setDurationDays] = useState('30');
  const [txType, setTxType] = useState<GrantTxType>('promo');
  const [created, setCreated] = useState<GrantDTO[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  // `VoucherPrefix` is 2–5 letters (docs/07 §3). Without this the drawer sent "QC3", the API
  // answered 422 `validation_error` and the operator only saw "Something went wrong" (QA3
  // CMS-66) — so the rule is checked where it is typed and named on the field itself.
  const prefixValid = VoucherPrefix.safeParse(prefix).success;

  const generate = useMutation({
    mutationFn: () =>
      api.cmsGrantCreate({
        body: {
          mode: 'vouchers',
          count: Number(count) || 1,
          prefix,
          txType,
          durationDays: durationDays.trim() ? Number(durationDays) : null,
        },
      }),
    onSuccess: (result) => {
      if (result.mode !== 'vouchers') return;
      setCreated(result.grants);
      // UX-562: every mutation outcome goes through the live region, creations included.
      toast({ message: t('cms:grants.toast.vouchers', { count: result.grants.length }) });
      void queryClient.invalidateQueries({ queryKey: ['cms', 'grants'] });
    },
    onError: () => setError(t('common:states.error')),
  });

  const codes = created?.map((grant) => grant.matchValue) ?? [];

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('cms:grants.vouchers.title')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
          <Button disabled={generate.isPending || !prefixValid} onClick={() => generate.mutate()}>
            {t('cms:grants.vouchers.generate')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('cms:grants.vouchers.count')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={1}
                max={1000}
                value={count}
                onChange={(e) => setCount(e.target.value)}
              />
            )}
          </Field>
          <Field
            label={t('cms:grants.vouchers.prefix')}
            help={t('cms:grants.vouchers.prefixHelp')}
            {...(prefixValid ? {} : { error: t('cms:grants.vouchers.prefixInvalid') })}
          >
            {(id) => (
              <Input
                id={id}
                value={prefix}
                maxLength={5}
                pattern="[A-Za-z]{2,5}"
                aria-invalid={!prefixValid}
                data-testid="voucher-prefix"
                onChange={(e) => setPrefix(e.target.value.toUpperCase())}
              />
            )}
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('cms:grants.form.durationDays')} help={t('cms:grants.form.durationHelp')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={1}
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('cms:grants.form.txType')}>
            {(id) => (
              <Select
                id={id}
                value={txType}
                onChange={(e) => setTxType(e.target.value as GrantTxType)}
              >
                {TX_TYPES.map((tx) => (
                  <option key={tx} value={tx}>
                    {t(`cms:grants.tx.${tx}` as 'cms:grants.tx.manual')}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        {created && (
          <section className="rounded-[12px] border border-line" data-testid="voucher-codes">
            <header className="flex items-center justify-between border-b border-line px-3 py-2">
              <h3 className="text-[13px] font-bold text-ink">
                {t('cms:grants.vouchers.generated', { count: codes.length })}
              </h3>
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  void navigator.clipboard?.writeText(codes.join('\n'));
                  setCopied(true);
                }}
              >
                {copied ? t('cms:grants.vouchers.copied') : t('cms:grants.vouchers.copy')}
              </Button>
            </header>
            <ul className="max-h-72 overflow-y-auto px-3 py-2 font-mono text-[13px] text-ink">
              {codes.map((code) => (
                <li key={code} className="py-0.5">
                  {code}
                </li>
              ))}
            </ul>
          </section>
        )}

        {error && (
          <p className="text-[12px] text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
}
